import { Link } from "react-router-dom";
import { ArrowRight, Check, Download, MessageCircle } from "lucide-react";
import { mediaUrl } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { useBi } from "../../lib/customer";
import { useSite } from "../../lib/site";
import { waLink } from "../../lib/format";
import { Reveal } from "../Common";
import { useNum } from "./Kit";

function Mix({ m }) {
  const b = useBi();
  const n = useNum();
  const label = m.key === "annex"
    ? b("Annex residences with private roof terrace", "ملاحق مع سطح خاص")
    : b(`Typical-floor ${m.bedrooms}-bedroom residences`, `شقق ${n(m.bedrooms)} غرف نوم في الأدوار المتكررة`);
  return (
    <div data-testid={`spotlight-mix-${m.key}`} className="border-t border-fg/15 pt-5">
      <p className="font-display text-4xl text-cta">{n(m.count)}</p>
      <p className="mt-1 text-sm text-fg/80">{label}</p>
      <p className="mt-1 text-xs text-fg/50" dir="ltr">{n(m.min_sqm)}–{n(m.max_sqm)} {b("m²", "م²")}</p>
    </div>
  );
}

export function Spotlight({ spot }) {
  const b = useBi();
  const { tr, lang } = useI18n();
  const { settings } = useSite();
  if (!spot) return null;
  const p = spot.project;
  const highlights = (p[`location_highlights_${lang}`] || p.location_highlights_en || []).slice(0, 4);
  return (
    <section data-testid="home-spotlight" className="on-media relative overflow-hidden bg-surface py-24 lg:py-32">
      <div className="mashrabiya pointer-events-none absolute inset-0 opacity-[0.04]" aria-hidden="true" />
      <div className="container-lux relative grid gap-12 lg:grid-cols-12 lg:gap-16">
        <Reveal className="relative lg:col-span-7">
          <div className="relative aspect-[4/3] overflow-hidden rounded-sm border border-fg/10 lg:aspect-auto lg:h-full lg:min-h-[560px]">
            <img src={mediaUrl(p.cover_image)} alt={tr(p, "title")} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-canvas/70 via-transparent to-transparent" aria-hidden="true" />
            <span data-testid="spotlight-status" className="absolute start-4 top-4 inline-flex items-center gap-2 rounded-full bg-canvas/80 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-fg backdrop-blur">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cta opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-cta" /></span>
              {tr(p, "status")}
            </span>
          </div>
        </Reveal>
        <Reveal delay={0.12} className="flex flex-col justify-center lg:col-span-5">
          <p className="eyebrow mb-5">{b("Flagship Project", "المشروع الرائد")}</p>
          <h2 data-testid="spotlight-title" className="h-display text-3xl leading-tight sm:text-4xl lg:text-5xl">{b("Al Hamra, Jeddah", "حي الحمراء، جدة")}</h2>
          <p className="mt-5 text-base text-fg/70">{tr(p, "summary")}</p>
          <ul className="mt-7 space-y-3" data-testid="spotlight-highlights">
            {highlights.map((h) => (<li key={h} className="flex items-start gap-3 text-sm text-fg/80"><Check className="mt-0.5 h-4 w-4 shrink-0 text-cta" />{h}</li>))}
          </ul>
          <div className="mt-8 grid grid-cols-2 gap-6" data-testid="spotlight-unit-mix">{spot.unit_mix.map((m) => (<Mix key={m.key} m={m} />))}</div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to={`/developments/${p.slug}`} data-testid="spotlight-view-project" className="btn-cta">{b("View Project", "عرض المشروع")}<ArrowRight className="rtl-flip h-4 w-4" /></Link>
            {p.brochure_url && <a href={mediaUrl(p.brochure_url)} target="_blank" rel="noopener noreferrer" data-testid="spotlight-brochure" className="btn-ghost"><Download className="h-4 w-4" />{b("Download Brochure", "تحميل الكتيّب")}</a>}
            {settings?.whatsapp && <a href={waLink(settings.whatsapp, `${b("Hello, I'm interested in", "مرحباً، أنا مهتم بـ")} ${tr(p, "title")}`)} target="_blank" rel="noopener noreferrer" data-testid="spotlight-whatsapp" className="btn-brand"><MessageCircle className="h-4 w-4" />{b("Inquire on WhatsApp", "استفسر عبر واتساب")}</a>}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
